import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { HomeLink } from "../components/HomeLink";
import { ProfileAvatar } from "../components/ProfileAvatar";
import { ProviderBadge } from "../components/ProviderBadge";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { authClient, fetchIdentityBadges } from "../lib/auth";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";

type AdminUser = {
  id: string;
  name: string;
  email: string;
  role?: string | null;
  banned?: boolean | null;
  banReason?: string | null;
  isAnonymous?: boolean | null;
  username?: string | null;
  createdAt?: Date | string | null;
};

export function AdminPage() {
  const identity = useAppStore((state) => state.auth);
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [badges, setBadges] = useState<
    Record<string, { name: string; provider: string; image: string | null }>
  >({});
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [banReason, setBanReason] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword2, setNewPassword2] = useState("");

  const isAdmin = identity?.role === "admin";

  const refreshUsers = useCallback(
    async (searchValue: string) => {
      setError(null);
      const result = await authClient.admin.listUsers({
        query: {
          limit: 50,
          sortBy: "createdAt",
          sortDirection: "desc",
          ...(searchValue.trim()
            ? {
                searchField: "email" as const,
                searchOperator: "contains" as const,
                searchValue: searchValue.trim(),
              }
            : {}),
        },
      });
      if (result.error) {
        setError(result.error.message || t("admin.actionFailed"));
        return;
      }
      const listed = (result.data?.users || []) as AdminUser[];
      setUsers(listed);
      // Resolve the same display name + badge the player sees.
      setBadges(
        await fetchIdentityBadges(listed.map((user) => user.id)).catch(
          () => ({}),
        ),
      );
    },
    [t],
  );

  useEffect(() => {
    if (!isAdmin) return;
    const timer = setTimeout(() => void refreshUsers(search), 300);
    return () => clearTimeout(timer);
  }, [isAdmin, search, refreshUsers]);

  if (!identity || !isAdmin) {
    return (
      <div className={`min-h-screen ${theme.bg}`}>
        <header className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <HomeLink />
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
        </header>
        <main className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className={`text-4xl font-light tracking-tight ${theme.text}`}>
            {t("admin.title")}
          </h1>
          <p className={`mt-4 ${theme.textMuted}`}>{t("admin.accessDenied")}</p>
        </main>
      </div>
    );
  }

  const run = async (key: string, action: () => Promise<unknown>) => {
    setPending(key);
    setError(null);
    try {
      await action();
      await refreshUsers(search);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : t("admin.actionFailed"),
      );
    } finally {
      setPending(null);
    }
  };

  const createUser = () =>
    run("create-user", async () => {
      const result = await authClient.admin.createUser({
        name: newName.trim(),
        email: newEmail.trim(),
        password: newPassword2,
      });
      if (result.error) throw new Error(result.error.message);
      setNewName("");
      setNewEmail("");
      setNewPassword2("");
    });

  const expand = (user: AdminUser) => {
    setExpandedId(expandedId === user.id ? null : user.id);
    setBanReason("");
    setNewPassword("");
  };

  const renderRow = (user: AdminUser) => {
    const expanded = expandedId === user.id;
    const identityName = badges[user.id]?.name || user.name;
    return (
      <div
        key={user.id}
        className={`rounded-xl border p-4 ${theme.border}`}
        data-testid="admin-user-row"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p
              className={`flex items-center gap-2 truncate font-medium ${theme.text}`}
            >
              <ProfileAvatar
                image={badges[user.id]?.image}
                name={identityName}
                size="sm"
              />
              <span className="truncate">{identityName}</span>
              <span data-testid={`admin-provider-badge-${user.id}`}>
                <ProviderBadge provider={badges[user.id]?.provider} />
              </span>
              {user.isAnonymous ? (
                <span className={`text-sm ${theme.textMuted}`}>
                  ({t("admin.guest")})
                </span>
              ) : null}
            </p>
            <p className={`truncate text-sm ${theme.textMuted}`}>
              {user.email}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid={`admin-role-badge-${user.id}`}
              className={`rounded-full border px-3 py-1 text-xs ${theme.border} ${theme.textMuted}`}
            >
              {user.role || "user"}
            </span>
            {user.banned ? (
              <span
                data-testid={`admin-banned-badge-${user.id}`}
                className={`rounded-full border px-3 py-1 text-xs ${theme.border} ${theme.errorText}`}
              >
                {t("admin.banned")}
              </span>
            ) : null}
            <Button
              data-testid={`admin-manage-btn-${user.id}`}
              type="button"
              variant="secondary"
              isDark={isDark}
              className="w-auto px-4 py-2 text-sm"
              onClick={() => expand(user)}
            >
              {expanded ? t("admin.close") : t("admin.manage")}
            </Button>
          </div>
        </div>

        {expanded && (
          <div
            className={`mt-4 space-y-3 border-t pt-4 ${theme.borderSubtle}`}
            data-testid={`admin-user-actions-${user.id}`}
          >
            <div className="flex flex-wrap gap-2">
              {(user.role || "user") === "admin" ? (
                <Button
                  data-testid={`admin-set-role-user-btn-${user.id}`}
                  type="button"
                  variant="secondary"
                  isDark={isDark}
                  className="w-auto px-4 py-2 text-sm"
                  disabled={pending !== null}
                  onClick={() =>
                    void run(`role-${user.id}`, async () => {
                      const result = await authClient.admin.setRole({
                        userId: user.id,
                        role: "user",
                      });
                      if (result.error) throw new Error(result.error.message);
                    })
                  }
                >
                  {t("admin.setRoleUser")}
                </Button>
              ) : (
                <Button
                  data-testid={`admin-set-role-admin-btn-${user.id}`}
                  type="button"
                  variant="secondary"
                  isDark={isDark}
                  className="w-auto px-4 py-2 text-sm"
                  disabled={pending !== null}
                  onClick={() =>
                    void run(`role-${user.id}`, async () => {
                      const result = await authClient.admin.setRole({
                        userId: user.id,
                        role: "admin",
                      });
                      if (result.error) throw new Error(result.error.message);
                    })
                  }
                >
                  {t("admin.setRoleAdmin")}
                </Button>
              )}

              {user.banned ? (
                <Button
                  data-testid={`admin-unban-btn-${user.id}`}
                  type="button"
                  variant="secondary"
                  isDark={isDark}
                  className="w-auto px-4 py-2 text-sm"
                  disabled={pending !== null}
                  onClick={() =>
                    void run(`unban-${user.id}`, async () => {
                      const result = await authClient.admin.unbanUser({
                        userId: user.id,
                      });
                      if (result.error) throw new Error(result.error.message);
                    })
                  }
                >
                  {t("admin.unban")}
                </Button>
              ) : (
                <>
                  <Input
                    data-testid={`admin-ban-reason-input-${user.id}`}
                    type="text"
                    placeholder={t("admin.banReason")}
                    className={`w-48 rounded-xl border px-3 py-2 text-sm ${theme.border} ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder}`}
                    value={banReason}
                    onChange={(event) => setBanReason(event.target.value)}
                  />
                  <Button
                    data-testid={`admin-ban-btn-${user.id}`}
                    type="button"
                    variant="secondary"
                    isDark={isDark}
                    className="w-auto px-4 py-2 text-sm"
                    disabled={pending !== null}
                    onClick={() =>
                      void run(`ban-${user.id}`, async () => {
                        const result = await authClient.admin.banUser({
                          userId: user.id,
                          ...(banReason.trim()
                            ? { banReason: banReason.trim() }
                            : {}),
                        });
                        if (result.error) throw new Error(result.error.message);
                      })
                    }
                  >
                    {t("admin.ban")}
                  </Button>
                </>
              )}

              <Button
                data-testid={`admin-revoke-sessions-btn-${user.id}`}
                type="button"
                variant="secondary"
                isDark={isDark}
                className="w-auto px-4 py-2 text-sm"
                disabled={pending !== null}
                onClick={() =>
                  void run(`revoke-${user.id}`, async () => {
                    const result = await authClient.admin.revokeUserSessions({
                      userId: user.id,
                    });
                    if (result.error) throw new Error(result.error.message);
                  })
                }
              >
                {t("admin.revokeSessions")}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Input
                data-testid={`admin-password-input-${user.id}`}
                type="password"
                placeholder={t("admin.newPassword")}
                className={`w-56 rounded-xl border px-3 py-2 text-sm ${theme.border} ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder}`}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
              <Button
                data-testid={`admin-set-password-btn-${user.id}`}
                type="button"
                variant="secondary"
                isDark={isDark}
                className="w-auto px-4 py-2 text-sm"
                disabled={pending !== null || newPassword.length < 8}
                onClick={() =>
                  void run(`password-${user.id}`, async () => {
                    const result = await authClient.admin.setUserPassword({
                      userId: user.id,
                      newPassword,
                    });
                    if (result.error) throw new Error(result.error.message);
                    setNewPassword("");
                  })
                }
              >
                {t("admin.setPassword")}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid={`admin-delete-btn-${user.id}`}
                type="button"
                variant="secondary"
                isDark={isDark}
                className={`w-auto px-4 py-2 text-sm ${theme.errorText}`}
                disabled={pending !== null}
                onClick={() =>
                  void run(`delete-${user.id}`, async () => {
                    const result = await authClient.admin.removeUser({
                      userId: user.id,
                    });
                    if (result.error) throw new Error(result.error.message);
                    setExpandedId(null);
                  })
                }
              >
                {t("admin.delete")}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      <header className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <HomeLink />
        <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
      </header>
      <main className="mx-auto max-w-3xl px-4 py-12">
        <h1 className={`text-4xl font-light tracking-tight ${theme.text}`}>
          {t("admin.title")}
        </h1>
        <p className={`mt-3 ${theme.textMuted}`}>{t("admin.subtitle")}</p>

        <section
          className={`mt-10 rounded-xl border p-6 ${theme.card} ${theme.border}`}
        >
          <h2 className={`text-xl ${theme.text}`}>{t("admin.createUser")}</h2>
          <div className="mt-4 space-y-3">
            <Input
              data-testid="admin-create-name-input"
              type="text"
              placeholder={t("admin.name")}
              className={`w-full rounded-xl border px-4 py-3 text-sm ${theme.border} ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder}`}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
            <Input
              data-testid="admin-create-email-input"
              type="email"
              placeholder={t("admin.email")}
              className={`w-full rounded-xl border px-4 py-3 text-sm ${theme.border} ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder}`}
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
            />
            <Input
              data-testid="admin-create-password-input"
              type="password"
              placeholder={t("admin.password")}
              className={`w-full rounded-xl border px-4 py-3 text-sm ${theme.border} ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder}`}
              value={newPassword2}
              onChange={(event) => setNewPassword2(event.target.value)}
            />
            <Button
              data-testid="admin-create-user-btn"
              type="button"
              variant="primary"
              isDark={isDark}
              disabled={
                pending !== null ||
                !newName.trim() ||
                !newEmail.trim() ||
                newPassword2.length < 8
              }
              onClick={() => void createUser()}
            >
              {t("admin.create")}
            </Button>
          </div>
        </section>

        <section className="mt-6">
          <h2 className={`text-xl ${theme.text}`}>{t("admin.users")}</h2>
          <Input
            data-testid="admin-user-search-input"
            type="search"
            placeholder={t("admin.search")}
            className={`mt-4 w-full rounded-xl border px-4 py-3 text-sm ${theme.border} ${theme.inputBg} ${theme.inputText} ${theme.inputPlaceholder}`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="mt-4 space-y-3" data-testid="admin-user-list">
            {users.length === 0 ? (
              <p className={`text-sm ${theme.textMuted}`}>
                {t("admin.noUsers")}
              </p>
            ) : (
              users.map(renderRow)
            )}
          </div>
        </section>

        {error && (
          <p
            data-testid="admin-error"
            role="alert"
            className={`mt-6 ${theme.errorText}`}
          >
            {error}
          </p>
        )}
      </main>
    </div>
  );
}
